import type { TaskNode } from "./types";
import { newTask } from "./model";
import { locate, locateOrNull, findParent, isDescendant, rollup } from "./tree";

// Pure tree transforms: each mutates the passed `tree` in place (and returns any
// id/flag the caller needs). Side-effects (save/render/focus) live in the view layer.

export function addRoot(tree: TaskNode[], defaultEstimate = 1): string {
  const t = newTask("New task", defaultEstimate);
  tree.push(t);
  return t.id;
}

export function addChild(tree: TaskNode[], id: string, defaultEstimate = 1): string | null {
  const loc = locateOrNull(tree, id);
  if (!loc) return null;
  const wasLeaf = loc.node.children.length === 0;
  loc.node.collapsed = false;
  const t = newTask("New subtask", defaultEstimate);
  // inherit the parent group's version/component so tasks added under a versioned/component
  // group don't start blank (copying "" is a harmless no-op)
  t.version = loc.node.version;
  t.component = loc.node.component;
  if (wasLeaf) {
    // leaf → parent: carry the parent's estimate (and per-stream breakdown) down to its first subtask
    t.estimate = loc.node.estimate || 0;
    t.estimates = loc.node.estimates;
    loc.node.estimate = 0;
    loc.node.estimates = {};
  }
  loc.node.children.push(t);
  return t.id;
}

// `before` inserts above the target instead of below — the caret-at-start case when Enter splits
// the outline, so a task can be added ahead of the first one without a move afterwards.
export function addSibling(tree: TaskNode[], id: string, defaultEstimate = 1, before = false): string | null {
  const loc = locateOrNull(tree, id);
  if (!loc) return null;
  const t = newTask("New task", defaultEstimate);
  // inherit the enclosing group's version/component (root-level siblings have no group → stay blank)
  const parent = findParent(tree, id);
  if (parent) {
    t.version = parent.parentNode.version;
    t.component = parent.parentNode.component;
  }
  loc.parentList.splice(before ? loc.index : loc.index + 1, 0, t);
  return t.id;
}

export function removeTask(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  const parent = findParent(tree, id); // must be read before the splice
  const removedEstimate = rollup(loc.node);
  loc.parentList.splice(loc.index, 1);
  if (parent && parent.parentNode.children.length === 0) {
    // last subtask removed: parent becomes a leaf again, carry the estimate back up
    // (as the single total; per-stream breakdown is reset — re-enter it if using streams)
    parent.parentNode.estimate = removedEstimate;
    parent.parentNode.estimates = {};
  }
}

export function move(tree: TaskNode[], id: string, dir: number): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  const j = loc.index + dir;
  if (j < 0 || j >= loc.parentList.length) return;
  const [n] = loc.parentList.splice(loc.index, 1);
  loc.parentList.splice(j, 0, n);
}

// indent: make this node a child of its previous sibling. Returns whether it moved.
export function indent(tree: TaskNode[], id: string): boolean {
  const loc = locateOrNull(tree, id);
  if (!loc || loc.index === 0) return false;
  const prev = loc.parentList[loc.index - 1];
  loc.parentList.splice(loc.index, 1);
  prev.collapsed = false;
  prev.children.push(loc.node);
  return true;
}

// outdent: move this node to be a sibling right after its parent. Returns whether it moved.
export function outdent(tree: TaskNode[], id: string): boolean {
  const parentLoc = findParent(tree, id);
  if (!parentLoc) return false; // already at root
  const { parentNode, grandList, parentIndex } = parentLoc;
  const childLoc = locate(parentNode.children, id);
  parentNode.children.splice(childLoc.index, 1);
  grandList.splice(parentIndex + 1, 0, childLoc.node);
  return true;
}

// move a node to be a child of target (append) or sibling before/after target
export function reparent(
  tree: TaskNode[],
  dragId: string,
  targetId: string,
  position: "into" | "before" | "after",
): void {
  if (dragId === targetId) return;
  if (isDescendant(tree, dragId, targetId)) return; // can't drop into own subtree
  const dragLoc = locateOrNull(tree, dragId);
  if (!dragLoc) return;
  const [node] = dragLoc.parentList.splice(dragLoc.index, 1);

  const tLoc = locateOrNull(tree, targetId);
  if (!tLoc) return;
  if (position === "into") {
    tLoc.node.collapsed = false;
    tLoc.node.children.push(node);
  } else {
    const idx = position === "before" ? tLoc.index : tLoc.index + 1;
    tLoc.parentList.splice(idx, 0, node);
  }
}

export function toggleCollapse(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.collapsed = !loc.node.collapsed;
}

export function toggleDisabled(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.disabled = !loc.node.disabled;
}

export function setName(tree: TaskNode[], id: string, value: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.name = value;
}

export function setEstimate(tree: TaskNode[], id: string, value: string | number): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  const v = parseFloat(String(value));
  loc.node.estimate = Number.isFinite(v) && v >= 0 ? v : 0;
}

// coerce a user-entered value to a non-negative number (blank/junk → 0)
function num(value: string | number): number {
  const v = parseFloat(String(value));
  return Number.isFinite(v) && v >= 0 ? v : 0;
}
// sum a node's per-stream values over the currently-defined estimations
function sumStreams(node: TaskNode, estimations: string[]): number {
  return estimations.reduce((s, name) => s + (node.estimates[name] || 0), 0);
}

// set one named stream's value on a task and refresh its effective total (`estimate` = sum
// over the defined streams). Keeps the invariant every pure reader relies on.
export function setEstimateStream(tree: TaskNode[], id: string, name: string, value: string | number, estimations: string[]): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  loc.node.estimates[name] = num(value);
  loc.node.estimate = sumStreams(loc.node, estimations);
}

// Re-derive every leaf's `estimate` from its per-stream map, keeping estimate == Σ streams.
// A leaf that has NO value yet for any defined stream but carries a plain estimate (a legacy
// single estimate, or a freshly-added task) gets that estimate seeded into the FIRST stream —
// so enabling streams never blanks out / zeroes existing estimates. No-op with no streams.
export function resyncEstimateTotals(tree: TaskNode[], estimations: string[]): void {
  if (!estimations.length) return;
  const first = estimations[0];
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if (n.children.length === 0) {
        const hasAny = estimations.some((s) => s in n.estimates);
        if (!hasAny && n.estimate > 0) n.estimates[first] = n.estimate; // migrate legacy/plain estimate
        n.estimate = sumStreams(n, estimations);
      } else walk(n.children);
    }
  })(tree);
}

export function setTag(tree: TaskNode[], id: string, value: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.tag = (value || "").trim();
}

export function setJiraKey(tree: TaskNode[], id: string, value: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.jiraKey = value || "";
}

export function addLink(tree: TaskNode[], id: string, value: string): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  const v = (value || "").trim();
  if (!v) return;
  if (!Array.isArray(loc.node.links)) loc.node.links = [];
  if (!loc.node.links.includes(v)) loc.node.links.push(v);
}

export function removeLink(tree: TaskNode[], id: string, value: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.links = (loc.node.links || []).filter((t) => t !== value);
}

export function setDone(tree: TaskNode[], id: string, value: boolean): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.done = !!value;
}

export function toggleQuestions(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.questions = !loc.node.questions;
}

export function togglePinned(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (loc) loc.node.pinned = !loc.node.pinned;
}

const COMPLEXITY_ORDER: TaskNode["complexity"][] = ["none", "easy", "medium", "hard"];
// rotate a task's complexity: none → easy → medium → hard → none
export function cycleComplexity(tree: TaskNode[], id: string): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  const i = COMPLEXITY_ORDER.indexOf(loc.node.complexity);
  loc.node.complexity = COMPLEXITY_ORDER[(i + 1) % COMPLEXITY_ORDER.length];
}

// fields that behave like version: a per-task label with the same cascade UX
export type CascadeField = "version" | "component";

// set a task's version/component. Cascade mode for descendants:
//   "matching" (default) — only descendants that shared this node's previous value
//   "all"                — every descendant
// `scope` (optional) restricts the cascade to descendants whose id is in the set (e.g. the tasks
// currently visible under a filter); the node itself is always set.
export function setField(tree: TaskNode[], id: string, field: CascadeField, value: string, mode: "matching" | "all" = "matching", scope?: Set<string>): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  const old = loc.node[field] || "";
  const v = value || "";
  loc.node[field] = v;
  (function walk(list: TaskNode[]) {
    list.forEach((n) => {
      const inScope = !scope || scope.has(n.id);
      if (inScope && (mode === "all" || (n[field] || "") === old)) n[field] = v;
      walk(n.children);
    });
  })(loc.node.children);
}

export const setVersion = (tree: TaskNode[], id: string, value: string, mode: "matching" | "all" = "matching", scope?: Set<string>) =>
  setField(tree, id, "version", value, mode, scope);
export const setComponent = (tree: TaskNode[], id: string, value: string, mode: "matching" | "all" = "matching", scope?: Set<string>) =>
  setField(tree, id, "component", value, mode, scope);

// batch-set `done` on the active descendant LEAVES of a group (parents derive their done state).
// `scope` restricts to in-scope leaves (e.g. the visible ones under a filter).
export function setDoneCascade(tree: TaskNode[], id: string, value: boolean, scope?: Set<string>): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if (n.disabled) continue;
      if (n.children.length === 0) { if (!scope || scope.has(n.id)) n.done = value; }
      else walk(n.children);
    }
  })(loc.node.children);
}

// batch-set `disabled` for a group. Without `scope`, toggle the group's own flag (the subtree is
// then excluded via inheritance). With `scope`, set the flag on each in-scope descendant node only,
// leaving the group (and hidden descendants) untouched — so a filter disables just the visible tasks.
export function setDisabledCascade(tree: TaskNode[], id: string, value: boolean, scope?: Set<string>): void {
  const loc = locateOrNull(tree, id);
  if (!loc) return;
  if (!scope) { loc.node.disabled = value; return; }
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (scope.has(n.id)) n.disabled = value; walk(n.children); }
  })(loc.node.children);
}

// does any descendant carry a value different from this node's for the given field?
// (used to decide whether changing a group needs the all/matching prompt)
export function subtreeHasOtherField(node: TaskNode, field: CascadeField): boolean {
  const ref = node[field] || "";
  let found = false;
  (function walk(list: TaskNode[]) {
    for (const n of list) { if ((n[field] || "") !== ref) found = true; walk(n.children); }
  })(node.children);
  return found;
}
export const subtreeHasOtherVersion = (node: TaskNode) => subtreeHasOtherField(node, "version");
